import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  Divider,
  CircularProgress,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  Chip,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { Formik } from "formik";
import * as yup from "yup";
import { DateTime } from "luxon";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { DrawerForm } from "../Common/styles/drawer";
import {
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { useAppSelector } from "../../Redux/hooks";

interface GenerateSlotsModalProps {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
}

export interface AdditionalBreak {
  startTime?: string;
  duration?: number;
}

export interface GenerateSlotsFormValues {
  startDate: string;
  endDate: string;
  slotDuration: number;
  startTime: string;
  endTime: string;
  lunchStartTime: string;
  lunchDuration: number;
  breakDuration: number;
  daysOfWeek: string[];
  additionalBreaks: AdditionalBreak[];
}

const daysOfWeekOptions = [
  { value: "1", label: "Sun" },
  { value: "2", label: "Mon" },
  { value: "3", label: "Tue" },
  { value: "4", label: "Wed" },
  { value: "5", label: "Thu" },
  { value: "6", label: "Fri" },
  { value: "7", label: "Sat" },
];

const schema = yup.object({
  startDate: yup.string().required("Start date is required"),
  endDate: yup
    .string()
    .required("End date is required")
    .test(
      "is-after-start",
      "End date must be after or equal to start date",
      function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        const start = DateTime.fromFormat(startDate, "MM/dd/yyyy");
        const end = DateTime.fromFormat(value, "MM/dd/yyyy");
        return end >= start;
      }
    ),
  slotDuration: yup
    .number()
    .required("Slot duration is required")
    .min(1, "Slot duration must be at least 1 minute")
    .max(1440, "Slot duration cannot exceed 24 hours"),
  startTime: yup.string().required("Day start time is required"),
  endTime: yup
    .string()
    .required("Day end time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (value) {
        const { startTime } = this.parent;
        if (!value || !startTime) return true;
        const start = DateTime.fromFormat(startTime, "HH:mm");
        const end = DateTime.fromFormat(value, "HH:mm");
        const diffInMinutes: any = end
          .diff(start, "minutes")
          .toObject().minutes;
        return diffInMinutes > 0;
      }
    ),
  lunchStartTime: yup
    .string()
    .required("Lunch start time is required")
    .test(
      "is-within-day",
      "Lunch start must be within day hours",
      function (value) {
        const { startTime, endTime } = this.parent;
        if (!value || !startTime || !endTime) return true;
        const lunchStart = DateTime.fromFormat(value, "HH:mm");
        const dayStart = DateTime.fromFormat(startTime, "HH:mm");
        const dayEnd = DateTime.fromFormat(endTime, "HH:mm");
        return lunchStart >= dayStart && lunchStart < dayEnd;
      }
    ),
  lunchDuration: yup
    .number()
    .required("Lunch duration is required")
    .min(1, "Lunch duration must be at least 1 minute")
    .test(
      "lunch-end-within-day",
      "Lunch end time must be within day hours",
      function (value) {
        const { lunchStartTime, startTime, endTime } = this.parent;
        if (!value || !lunchStartTime || !startTime || !endTime) return true;
        const lunchStart = DateTime.fromFormat(lunchStartTime, "HH:mm");
        const lunchEnd = lunchStart.plus({ minutes: value });
        const dayStart = DateTime.fromFormat(startTime, "HH:mm");
        const dayEnd = DateTime.fromFormat(endTime, "HH:mm");
        return lunchEnd > dayStart && lunchEnd <= dayEnd;
      }
    ),
  breakDuration: yup
    .number()
    .required("Break duration is required")
    .min(0, "Break duration cannot be negative")
    .max(120, "Break duration cannot exceed 2 hours"),
  daysOfWeek: yup
    .array()
    .of(yup.string())
    .min(1, "Select at least one day")
    .required("Days of week are required"),
  additionalBreaks: yup
    .array()
    .of(
      yup.object().shape({
        startTime: yup
          .string()
          .required("Break start time is required")
          .test(
            "is-within-day",
            "Break start must be within day hours",
            function (value) {
              const root = this.from?.[1]?.value || this.from?.[0]?.value;
              const startTime = root?.startTime;
              const endTime = root?.endTime;
              if (!value || !startTime || !endTime) return true;
              const breakStart = DateTime.fromFormat(value, "HH:mm");
              const dayStart = DateTime.fromFormat(startTime, "HH:mm");
              const dayEnd = DateTime.fromFormat(endTime, "HH:mm");
              return breakStart >= dayStart && breakStart < dayEnd;
            }
          ),
        duration: yup
          .number()
          .required("Break duration is required")
          .min(1, "Break duration must be at least 1 minute")
          .test(
            "break-end-within-day",
            "Break end time must be within day hours",
            function (value) {
              const { startTime } = this.parent;
              const root = this.from?.[1]?.value || this.from?.[0]?.value;
              const dayStartTime = root?.startTime;
              const dayEndTime = root?.endTime;
              if (!value || !startTime || !dayStartTime || !dayEndTime)
                return true;
              const breakStart = DateTime.fromFormat(startTime, "HH:mm");
              const breakEnd = breakStart.plus({ minutes: value });
              const dayStart = DateTime.fromFormat(dayStartTime, "HH:mm");
              const dayEnd = DateTime.fromFormat(dayEndTime, "HH:mm");
              return breakEnd > dayStart && breakEnd <= dayEnd;
            }
          ),
      })
    )
    .max(3, "Maximum 3 additional breaks allowed"),
});

interface SimulatedSlot {
  roomId: string;
  startDateTime: string;
  endDateTime: string;
}

interface FormattedSlot {
  roomId: string;
  date: string;
  startTime: string;
  endTime: string;
  fullStartDateTime: string;
  fullEndDateTime: string;
}

interface GroupedSlots {
  date: string;
  slots: FormattedSlot[];
}

// Helper component to track form changes
const FormChangeTracker: React.FC<{
  values: GenerateSlotsFormValues;
  setHasFormChanged: (value: boolean) => void;
}> = ({ values, setHasFormChanged }) => {
  const firstMount = React.useRef(true);

  useEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }
    setHasFormChanged(true);
  }, [
    values.startDate,
    values.endDate,
    values.slotDuration,
    values.startTime,
    values.endTime,
    values.lunchStartTime,
    values.lunchDuration,
    values.breakDuration,
    values.daysOfWeek,
    values.additionalBreaks,
    setHasFormChanged,
  ]);

  return <></>;
};

const GenerateSlotsModal: React.FC<GenerateSlotsModalProps> = ({
  open,
  onClose,
  refresh,
}) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [simulatedSlots, setSimulatedSlots] = useState<GroupedSlots[] | null>(
    null
  );
  const [simulateLoader, setSimulateLoader] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const selectedSite = useAppSelector(
    (state) => state.appointment.selectedSite
  );

  const buildRequestBody = (values: GenerateSlotsFormValues) => {
    const daysOfWeek = [0, 0, 0, 0, 0, 0, 0];
    values.daysOfWeek.forEach((day) => {
      const index = parseInt(day, 10) - 1;
      daysOfWeek[index] = 1;
    });

    // Calculate lunch end time from start time and duration
    const lunchStartTime = DateTime.fromFormat(values.lunchStartTime, "HH:mm");
    const lunchEndTime = lunchStartTime.plus({ minutes: values.lunchDuration });

    // Build breaks array with lunch and additional breaks
    const breaks = [
      {
        start: values.lunchStartTime,
        end: lunchEndTime.toFormat("HH:mm"),
      },
    ];

    // Add additional breaks if they have both startTime and duration
    values.additionalBreaks.forEach((breakItem) => {
      if (breakItem.startTime && breakItem.duration) {
        const startTime = DateTime.fromFormat(breakItem.startTime, "HH:mm");
        const endTime = startTime.plus({ minutes: breakItem.duration });
        breaks.push({
          start: breakItem.startTime,
          end: endTime.toFormat("HH:mm"),
        });
      }
    });

    return {
      startDate: DateTime.fromFormat(values.startDate, "MM/dd/yyyy").toFormat(
        "yyyy-MM-dd"
      ),
      endDate: DateTime.fromFormat(values.endDate, "MM/dd/yyyy").toFormat(
        "yyyy-MM-dd"
      ),
      slotDuration: values.slotDuration,
      startTime: values.startTime,
      endTime: values.endTime,
      breaks,
      breakBetweenSlots: values.breakDuration,
      daysOfWeek,
    };
  };

  const simulateHandler = async (values: GenerateSlotsFormValues) => {
    try {
      setSimulateLoader(true);
      const body = buildRequestBody(values);
      const roomId = sessionStorage.getItem("reg_appt_room");
      if (!roomId) {
        toastMessage("error", "No room selected");
        return;
      }
      const response = await http.post(
        `/admin/registry/appointment-rooms/${roomId}/slots/simulate`,
        body
      );
      const rawSlots: SimulatedSlot[] = response.data?.data?.data || [];

      // Format and group dates after fetching
      const formattedSlots: FormattedSlot[] = rawSlots.map((slot) => {
        const siteTimezone =
          selectedSite?.timezone || DateTime.local().zoneName;
        const startDateTime = DateTime.fromISO(slot.startDateTime, {
          zone: siteTimezone,
        });
        const endDateTime = DateTime.fromISO(slot.endDateTime, {
          zone: siteTimezone,
        });

        return {
          roomId: slot.roomId,
          date: startDateTime.toFormat("MM/dd/yyyy"),
          startTime: startDateTime.toFormat("hh:mm a"),
          endTime: endDateTime.toFormat("hh:mm a"),
          fullStartDateTime: startDateTime.toFormat("MM/dd/yyyy hh:mm a"),
          fullEndDateTime: endDateTime.toFormat("MM/dd/yyyy hh:mm a"),
        };
      });

      // Group slots by date
      const groupedByDate = formattedSlots.reduce((acc, slot) => {
        const existingGroup = acc.find((g) => g.date === slot.date);
        if (existingGroup) {
          existingGroup.slots.push(slot);
        } else {
          acc.push({ date: slot.date, slots: [slot] });
        }
        return acc;
      }, [] as GroupedSlots[]);

      setSimulatedSlots(groupedByDate);
      setHasFormChanged(false);
    } catch (err) {
      setSimulatedSlots(null);
      errorToastMessage(err as Error);
    } finally {
      setSimulateLoader(false);
    }
  };

  const submitHandler = async (values: GenerateSlotsFormValues) => {
    try {
      setSubmitLoader(true);
      const body = buildRequestBody(values);
      const roomId = sessionStorage.getItem("reg_appt_room");
      if (!roomId) {
        toastMessage("error", "No room selected");
        return;
      }
      const res = await http.post(
        `/admin/registry/appointment-rooms/${roomId}/slots/generate`,
        body
      );
      refresh();
      onClose();
      setSimulatedSlots(null);
      toastMessage("success", res.data.message);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleSimulateWithValidation = async (
    values: GenerateSlotsFormValues,
    validateForm: () => Promise<any>,
    setTouched: (fields: any) => void
  ) => {
    // Validate all fields
    const validationErrors = await validateForm();

    // Set all fields as touched to show errors
    const touchedFields: any = {
      startDate: true,
      endDate: true,
      slotDuration: true,
      startTime: true,
      endTime: true,
      lunchStartTime: true,
      lunchDuration: true,
      breakDuration: true,
      daysOfWeek: true,
      additionalBreaks: values.additionalBreaks.map(() => ({
        startTime: true,
        duration: true,
      })),
    };
    setTouched(touchedFields);

    // Only proceed if validation passes
    if (Object.keys(validationErrors).length === 0) {
      simulateHandler(values);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={"Generate Appointment Slots"}
          onCloseClick={onClose}
        />
        <Formik
          initialValues={{
            startDate: DateTime.now().toFormat("MM/dd/yyyy"),
            endDate: DateTime.now().plus({ weeks: 1 }).toFormat("MM/dd/yyyy"),
            slotDuration: 45,
            startTime: "09:00",
            endTime: "17:00",
            lunchStartTime: "12:00",
            lunchDuration: 60,
            breakDuration: 5,
            daysOfWeek: ["2", "3", "4", "5", "6"], // Mon-Fri by default
            additionalBreaks: [],
          }}
          enableReinitialize
          onSubmit={(values: GenerateSlotsFormValues) => {
            submitHandler(values);
          }}
          validationSchema={schema}
        >
          {({
            errors,
            touched,
            values,
            setFieldValue,
            getFieldProps,
            handleSubmit,
            validateForm,
            setTouched,
          }) => (
            <form onSubmit={handleSubmit} style={DrawerForm}>
              <Box>
                {simulatedSlots && (
                  <FormChangeTracker
                    values={values}
                    setHasFormChanged={setHasFormChanged}
                  />
                )}

                {/* Date Range */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>Start Date</FormLabel>
                    <DatePicker
                      disablePast
                      format="MM/dd/yyyy"
                      value={
                        values.startDate
                          ? DateTime.fromFormat(values.startDate, "MM/dd/yyyy")
                          : null
                      }
                      onChange={(newValue: any) => {
                        if (newValue) {
                          setFieldValue(
                            "startDate",
                            newValue.toFormat("MM/dd/yyyy")
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          inputProps: {
                            readOnly: true,
                            placeholder: "Select start date",
                            id: "startDate",
                          },
                          error: touched.startDate && Boolean(errors.startDate),
                          helperText:
                            touched.startDate && errors.startDate
                              ? (errors.startDate as string)
                              : " ",
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>End Date</FormLabel>
                    <DatePicker
                      disablePast
                      format="MM/dd/yyyy"
                      value={
                        values.endDate
                          ? DateTime.fromFormat(values.endDate, "MM/dd/yyyy")
                          : null
                      }
                      onChange={(newValue: any) => {
                        if (newValue) {
                          setFieldValue(
                            "endDate",
                            newValue.toFormat("MM/dd/yyyy")
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          inputProps: {
                            readOnly: true,
                            placeholder: "Select end date",
                            id: "endDate",
                          },
                          error: touched.endDate && Boolean(errors.endDate),
                          helperText:
                            touched.endDate && errors.endDate
                              ? (errors.endDate as string)
                              : " ",
                        },
                      }}
                    />
                  </FormControl>
                </Box>

                {/* Slot Duration */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="slotDuration">
                    Slot Duration (minutes)
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="slotDuration"
                    type="number"
                    {...getFieldProps("slotDuration")}
                    error={touched.slotDuration && Boolean(errors.slotDuration)}
                    helperText={
                      touched.slotDuration && errors.slotDuration
                        ? (errors.slotDuration as string)
                        : " "
                    }
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                </FormControl>

                {/* Day Time Range */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>Day Start Time</FormLabel>
                    <TimePicker
                      ampm={false}
                      format="hh:mm a"
                      value={
                        values.startTime
                          ? DateTime.fromFormat(values.startTime, "HH:mm")
                          : null
                      }
                      onChange={(newValue: any) => {
                        if (newValue) {
                          setFieldValue(
                            "startTime",
                            newValue.toFormat("HH:mm")
                          );
                        }
                      }}
                      slotProps={{
                        textField: {
                          inputProps: {
                            placeholder: "Select start time",
                            id: "startTime",
                          },
                        },
                      }}
                      sx={{
                        border:
                          touched.startTime && errors.startTime
                            ? "1px solid red"
                            : undefined,
                        borderRadius:
                          touched.startTime && errors.startTime
                            ? "8px"
                            : undefined,
                      }}
                    />
                    <FormHelperText
                      error={Boolean(touched.startTime && errors.startTime)}
                    >
                      {touched.startTime && errors.startTime
                        ? (errors.startTime as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>Day End Time</FormLabel>
                    <TimePicker
                      ampm={false}
                      format="hh:mm a"
                      value={
                        values.endTime
                          ? DateTime.fromFormat(values.endTime, "HH:mm")
                          : null
                      }
                      onChange={(newValue: any) => {
                        if (newValue) {
                          setFieldValue("endTime", newValue.toFormat("HH:mm"));
                        }
                      }}
                      minTime={
                        values.startTime
                          ? DateTime.fromFormat(values.startTime, "HH:mm").plus(
                              {
                                minutes: 1,
                              }
                            )
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          inputProps: {
                            placeholder: "Select end time",
                            id: "endTime",
                          },
                        },
                      }}
                      sx={{
                        border:
                          touched.endTime && errors.endTime
                            ? "1px solid red"
                            : undefined,
                        borderRadius:
                          touched.endTime && errors.endTime ? "8px" : undefined,
                      }}
                    />
                    <FormHelperText
                      error={Boolean(touched.endTime && errors.endTime)}
                    >
                      {touched.endTime && errors.endTime
                        ? (errors.endTime as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                </Box>

                {/* Lunch Settings */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>Lunch Start Time</FormLabel>
                    <TimePicker
                      ampm={false}
                      format="hh:mm a"
                      value={
                        values.lunchStartTime
                          ? DateTime.fromFormat(values.lunchStartTime, "HH:mm")
                          : null
                      }
                      onChange={(newValue: any) => {
                        if (newValue) {
                          setFieldValue(
                            "lunchStartTime",
                            newValue.toFormat("HH:mm")
                          );
                        }
                      }}
                      minTime={
                        values.startTime
                          ? DateTime.fromFormat(values.startTime, "HH:mm")
                          : undefined
                      }
                      maxTime={
                        values.endTime
                          ? DateTime.fromFormat(values.endTime, "HH:mm").minus({
                              minutes: 1,
                            })
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          inputProps: {
                            placeholder: "Select lunch start",
                            id: "lunchStartTime",
                          },
                        },
                      }}
                      sx={{
                        border:
                          touched.lunchStartTime && errors.lunchStartTime
                            ? "1px solid red"
                            : undefined,
                        borderRadius:
                          touched.lunchStartTime && errors.lunchStartTime
                            ? "8px"
                            : undefined,
                      }}
                    />
                    <FormHelperText
                      error={Boolean(
                        touched.lunchStartTime && errors.lunchStartTime
                      )}
                    >
                      {touched.lunchStartTime && errors.lunchStartTime
                        ? (errors.lunchStartTime as string)
                        : " "}
                    </FormHelperText>
                  </FormControl>
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>
                      Lunch Duration (minutes)
                    </FormLabel>
                    <TextField
                      fullWidth
                      id="lunchDuration"
                      type="number"
                      {...getFieldProps("lunchDuration")}
                      error={
                        touched.lunchDuration && Boolean(errors.lunchDuration)
                      }
                      helperText={
                        touched.lunchDuration && errors.lunchDuration
                          ? (errors.lunchDuration as string)
                          : " "
                      }
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                  </FormControl>
                </Box>

                {/* Break Duration */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Break Duration Between Slots (minutes)
                  </FormLabel>
                  <TextField
                    fullWidth
                    id="breakDuration"
                    type="number"
                    {...getFieldProps("breakDuration")}
                    error={
                      touched.breakDuration && Boolean(errors.breakDuration)
                    }
                    helperText={
                      touched.breakDuration && errors.breakDuration
                        ? (errors.breakDuration as string)
                        : " "
                    }
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </FormControl>

                {/* Additional Breaks */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <FormLabel sx={LabelStyle}>
                      Additional Breaks (Optional)
                    </FormLabel>
                    {values.additionalBreaks.length < 3 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const newBreak: AdditionalBreak = {
                            startTime: undefined,
                            duration: undefined,
                          };
                          setFieldValue("additionalBreaks", [
                            ...values.additionalBreaks,
                            newBreak,
                          ]);
                        }}
                      >
                        Add Break
                      </Button>
                    )}
                  </Box>
                  {values.additionalBreaks.map(
                    (breakItem: AdditionalBreak, index) => {
                      const breakErrors =
                        (errors.additionalBreaks as any)?.[index] || {};
                      const breakTouched =
                        (touched.additionalBreaks as any)?.[index] || {};
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: 2,
                            mb: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          <FormControl sx={{ ...InputWrapper, flex: 1 }}>
                            <FormLabel sx={LabelStyle}>
                              Ideal Break {index + 1} Start Time
                            </FormLabel>
                            <TimePicker
                              ampm={false}
                              format="hh:mm a"
                              value={
                                breakItem.startTime
                                  ? DateTime.fromFormat(
                                      breakItem.startTime,
                                      "HH:mm"
                                    )
                                  : null
                              }
                              onChange={(newValue: any) => {
                                const updatedBreaks: AdditionalBreak[] = [
                                  ...values.additionalBreaks,
                                ];
                                if (newValue) {
                                  updatedBreaks[index] = {
                                    ...updatedBreaks[index],
                                    startTime: newValue.toFormat("HH:mm"),
                                  };
                                } else {
                                  updatedBreaks[index] = {
                                    ...updatedBreaks[index],
                                    startTime: undefined,
                                  };
                                }
                                setFieldValue(
                                  "additionalBreaks",
                                  updatedBreaks
                                );
                              }}
                              minTime={
                                values.startTime
                                  ? DateTime.fromFormat(
                                      values.startTime,
                                      "HH:mm"
                                    )
                                  : undefined
                              }
                              maxTime={
                                values.endTime
                                  ? DateTime.fromFormat(
                                      values.endTime,
                                      "HH:mm"
                                    ).minus({
                                      minutes: 1,
                                    })
                                  : undefined
                              }
                              slotProps={{
                                textField: {
                                  inputProps: {
                                    placeholder: "Select break start",
                                    id: `break-${index}-start`,
                                  },
                                },
                              }}
                              sx={{
                                border:
                                  breakTouched.startTime &&
                                  breakErrors.startTime
                                    ? "1px solid red"
                                    : undefined,
                                borderRadius:
                                  breakTouched.startTime &&
                                  breakErrors.startTime
                                    ? "8px"
                                    : undefined,
                              }}
                            />
                            <FormHelperText
                              error={Boolean(
                                breakTouched.startTime && breakErrors.startTime
                              )}
                            >
                              {breakTouched.startTime && breakErrors.startTime
                                ? (breakErrors.startTime as string)
                                : " "}
                            </FormHelperText>
                          </FormControl>
                          <FormControl sx={{ ...InputWrapper, flex: 1 }}>
                            <FormLabel sx={LabelStyle}>
                              Duration (minutes)
                            </FormLabel>
                            <TextField
                              fullWidth
                              type="number"
                              value={breakItem.duration || ""}
                              onChange={(e) => {
                                const updatedBreaks: AdditionalBreak[] = [
                                  ...values.additionalBreaks,
                                ];
                                updatedBreaks[index] = {
                                  ...updatedBreaks[index],
                                  duration:
                                    e.target.value === ""
                                      ? undefined
                                      : parseInt(e.target.value, 10),
                                };
                                setFieldValue(
                                  "additionalBreaks",
                                  updatedBreaks
                                );
                              }}
                              error={
                                breakTouched.duration &&
                                Boolean(breakErrors.duration)
                              }
                              helperText={
                                breakTouched.duration && breakErrors.duration
                                  ? (breakErrors.duration as string)
                                  : " "
                              }
                              InputProps={{ inputProps: { min: 1 } }}
                              placeholder="Duration in minutes"
                            />
                          </FormControl>
                          <IconButton
                            onClick={() => {
                              const updatedBreaks =
                                values.additionalBreaks.filter(
                                  (_, i) => i !== index
                                );
                              setFieldValue("additionalBreaks", updatedBreaks);
                            }}
                            sx={{ mt: 3 }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    }
                  )}
                  {touched.additionalBreaks && errors.additionalBreaks && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {typeof errors.additionalBreaks === "string"
                        ? errors.additionalBreaks
                        : ""}
                    </Typography>
                  )}
                </Box>

                {/* Days of Week */}
                <FormControl
                  sx={InputWrapper}
                  error={touched.daysOfWeek && Boolean(errors.daysOfWeek)}
                >
                  <FormLabel sx={LabelStyle} htmlFor="daysOfWeek">
                    Days of Week
                  </FormLabel>
                  <Select
                    id="daysOfWeek"
                    name="daysOfWeek"
                    multiple
                    value={values.daysOfWeek}
                    onChange={(event: SelectChangeEvent<string[]>) => {
                      const value = event.target.value;
                      setFieldValue(
                        "daysOfWeek",
                        typeof value === "string" ? value.split(",") : value
                      );
                    }}
                    input={<OutlinedInput label="Days of Week" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const day = daysOfWeekOptions.find(
                            (d) => d.value === value
                          );
                          return (
                            <Chip key={value} label={day?.label} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {daysOfWeekOptions.map((day) => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.daysOfWeek && errors.daysOfWeek && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {errors.daysOfWeek as string}
                    </Typography>
                  )}
                </FormControl>
              </Box>
              {simulatedSlots && (
                <Box sx={{ my: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="h6">
                      Simulated Slots ({selectedSite?.timezoneLabel})
                    </Typography>
                    <Chip
                      label={`${simulatedSlots.reduce(
                        (total, group) => total + group.slots.length,
                        0
                      )} slots`}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Box sx={{ overflowX: "auto", maxHeight: 300 }}>
                    <Table sx={TableBorderRadiusTopLeftRight}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Date</StyledTableCell>
                          <StyledTableCell>Time Ranges</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {simulatedSlots.map((group, groupIndex) => (
                          <TableRow key={groupIndex}>
                            <StyledTableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                }}
                              >
                                {group.date}
                                <Chip
                                  label={group.slots.length}
                                  size="small"
                                  sx={{ ml: 1, height: "20px" }}
                                />
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  py: 1,
                                }}
                              >
                                {group.slots.map((slot, slotIndex) => (
                                  <Chip
                                    key={slotIndex}
                                    label={`${slot.startTime} → ${slot.endTime}`}
                                    size="small"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "12px",
                                      backgroundColor: "#F3F4F6",
                                      border: "none",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))}
                              </Box>
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader && !simulateLoader ? (
                    <>
                      <Button onClick={onClose} variant="outlined">
                        Cancel
                      </Button>
                      {!simulatedSlots || hasFormChanged ? (
                        <Button
                          onClick={() => {
                            handleSimulateWithValidation(
                              values,
                              validateForm,
                              setTouched
                            );
                          }}
                          variant="outlined"
                        >
                          Simulate
                        </Button>
                      ) : (
                        <Button type="submit" variant="contained">
                          Generate
                        </Button>
                      )}
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default GenerateSlotsModal;
